import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ChartLineUp,
  Users,
  ClipboardText,
  CheckCircle,
  ArrowsClockwise,
  Lightning,
  WhatsappLogo,
  ArrowSquareOut,
  WarningCircle,
  PencilSimple,
  Tag,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/** Full-screen admin report page — Meta ads → WhatsApp lead → booking funnel. */
export default function AdPerformance() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firing, setFiring] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [tab, setTab] = useState("funnel"); // 'funnel' | 'ads'
  const [adsData, setAdsData] = useState(null);
  const [loadingAds, setLoadingAds] = useState(false);
  const [balance, setBalance] = useState(null); // { balance_rupees, amount_spent_rupees, currency, ... }
  const [autoSync, setAutoSync] = useState(() => {
    try { return localStorage.getItem("rb_ads_autosync") === "1"; } catch { return false; }
  });

  const loadBalance = async () => {
    try {
      const r = await api.get("/reports/account/balance");
      setBalance(r.data);
    } catch (_) {
      setBalance(null);
    }
  };

  const loadAds = async (window = `${days}d`) => {
    setLoadingAds(true);
    try {
      const w = window === "7d" || window === "30d" ? window : "7d";
      const r = await api.get(`/reports/ads/list?window=${w}`);
      setAdsData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ads");
    } finally {
      setLoadingAds(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const r = await api.get("/reports/campaigns/sync-status");
      setSyncStatus(r.data);
    } catch (_) {
      /* non-admin or network error — fine */
    }
  };

  const load = async (d = days) => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/ad-performance?days=${d}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    loadSyncStatus();
    loadBalance();
    if (tab === "ads") loadAds(`${days}d`);
    document.title = `Ad Performance · RodBez Whatsapp`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, tab]);

  // Persist auto-sync toggle + run a periodic refresh while ON.
  // Refresh every 60s — fetches list + balance (no full /sync call to avoid
  // hammering Meta's rate limits; user can press the manual Sync button).
  useEffect(() => {
    try { localStorage.setItem("rb_ads_autosync", autoSync ? "1" : "0"); } catch {}
    if (!autoSync) return;
    const id = setInterval(() => {
      load(days);
      loadBalance();
      if (tab === "ads") loadAds(`${days}d`);
    }, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSync, days, tab]);

  const fireTest = async () => {
    setFiring(true);
    try {
      const r = await api.post("/reports/capi/test", { phone: "919155394055" });
      if (r.data?.ok) {
        toast.success("Test Lead sent — check Meta Events Manager → Test Events");
      } else {
        toast.error(`Meta error: ${(r.data?.error || "").slice(0, 140)}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test fire failed");
    } finally {
      setFiring(false);
    }
  };

  const syncCampaigns = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/reports/campaigns/sync");
      toast.success(
        `Synced ${r.data.ads} ads · ${r.data.adsets} adsets · ${r.data.campaigns} campaigns · ${r.data.insights_upserts || 0} insight rows`,
      );
      await loadSyncStatus();
      await load(days);
      if (tab === "ads") await loadAds(`${days}d`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sync failed";
      toast.error(msg, { duration: 8000 });
    } finally {
      setSyncing(false);
    }
  };

  const t = data?.totals || {};

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)]" data-testid="ad-performance-page">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--surface)]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#1877F2] grid place-items-center text-white">
            <ChartLineUp weight="fill" size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="heading text-lg md:text-xl font-bold leading-tight">
              Ad Performance
            </div>
            <div className="text-xs text-[var(--muted)] leading-tight">
              Meta click-to-WhatsApp funnel · Pixel {data?.pixel_id || "—"}
              {syncStatus?.last_sync_at && (
                <>
                  {" · "}Names synced {new Date(syncStatus.last_sync_at).toLocaleString()}{" "}
                  ({syncStatus.cached_items} items)
                </>
              )}
              {!data?.capi_configured && " · CAPI not configured"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? "default" : "outline"}
                className="h-8 text-xs"
                onClick={() => setDays(d)}
                data-testid={`days-${d}`}
              >
                {d}d
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              onClick={() => load(days)}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
              <span className="hidden md:inline">Refresh</span>
            </Button>
            <CampaignNamesDialog onSaved={() => load(days)} />
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              onClick={syncCampaigns}
              disabled={syncing || !syncStatus?.configured}
              data-testid="sync-campaigns-btn"
              title={
                syncStatus?.last_sync_at
                  ? `Last synced: ${new Date(syncStatus.last_sync_at).toLocaleString()}`
                  : "Never synced"
              }
            >
              <ArrowsClockwise size={12} className={syncing ? "animate-spin" : ""} />
              <span className="hidden md:inline">
                {syncing ? "Syncing…" : "Sync names"}
              </span>
            </Button>
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-[#1877F2] hover:bg-[#166fe5] text-white"
              onClick={fireTest}
              disabled={firing || !data?.capi_configured}
              data-testid="fire-test-btn"
              title="Send a test Lead event to Meta — view in Events Manager → Test Events"
            >
              <Lightning size={12} weight="fill" />
              {firing ? "Firing…" : "Test event"}
            </Button>
            <label
              className="flex items-center gap-1.5 text-[11px] text-[var(--muted)] cursor-pointer select-none"
              title="Auto-refresh ads + balance every 60s"
            >
              <input
                type="checkbox"
                checked={autoSync}
                onChange={(e) => setAutoSync(e.target.checked)}
                className="h-3.5 w-3.5 cursor-pointer accent-[#1877F2]"
                data-testid="ads-autosync-toggle"
              />
              <span className="font-medium">Auto-sync</span>
            </label>
          </div>
        </div>
        {/* Account balance chip — color-coded with low-balance warning */}
        {balance && (
          <div
            className="max-w-7xl mx-auto px-4 md:px-6 pb-3 -mt-1 flex flex-wrap items-center gap-2 text-[12px]"
            data-testid="ads-account-balance"
          >
            <span className="text-[var(--muted)] font-medium">{balance.name}:</span>
            <span
              className={`px-2 py-0.5 rounded-md font-semibold border ${
                balance.balance_rupees != null && balance.balance_rupees < 200
                  ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B]"
                  : balance.balance_rupees != null && balance.balance_rupees < 500
                    ? "bg-[#FEF3C7] border-[#FDE68A] text-[#92400E]"
                    : "bg-[#D1FAE5] border-[#A7F3D0] text-[#065F46]"
              }`}
              title="Prepaid balance — credit card/UPI added me ke baad bhi remaining wallet"
            >
              💰 Balance: ₹{balance.balance_rupees?.toLocaleString("en-IN") ?? "—"}
            </span>
            <span className="px-2 py-0.5 rounded-md font-medium border border-[var(--border)] bg-[var(--surface)]">
              Spent: ₹{balance.amount_spent_rupees?.toLocaleString("en-IN")}
            </span>
            {balance.spend_cap_rupees != null && (
              <span className="px-2 py-0.5 rounded-md font-medium border border-[var(--border)] bg-[var(--surface)]">
                Cap: ₹{balance.spend_cap_rupees.toLocaleString("en-IN")}
              </span>
            )}
            {balance.funding_source && (
              <span className="px-2 py-0.5 rounded-md font-medium text-[var(--muted)]">
                💳 {balance.funding_source}
              </span>
            )}
            {balance.account_status !== 1 && (
              <span className="px-2 py-0.5 rounded-md font-bold bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5]">
                ⚠️ Account NOT active
              </span>
            )}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {!data?.capi_configured && (
          <div className="flex items-start gap-2 p-3 rounded-md border border-[#F59E0B]/40 bg-[#FEF3C7] text-[#78350F]">
            <WarningCircle size={18} className="shrink-0 mt-0.5" />
            <div className="text-xs">
              <b>Meta CAPI not configured.</b> Set <code>META_PIXEL_ID</code> and
              <code className="mx-1">META_CAPI_ACCESS_TOKEN</code> in backend/.env
              to start sending conversion events.
            </div>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex items-center gap-2 border-b border-[var(--border)]">
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === "funnel"
                ? "border-[#1877F2] text-[#1877F2]"
                : "border-transparent text-[var(--muted)] hover:text-[var(--fg)]"
            }`}
            onClick={() => setTab("funnel")}
            data-testid="tab-funnel"
          >
            📩 Our Funnel (Leads → Bookings)
          </button>
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === "ads"
                ? "border-[#1877F2] text-[#1877F2]"
                : "border-transparent text-[var(--muted)] hover:text-[var(--fg)]"
            }`}
            onClick={() => setTab("ads")}
            data-testid="tab-ads"
          >
            📢 Running Ads (Reach + Spend)
          </button>
          <button
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === "pixel"
                ? "border-[#1877F2] text-[#1877F2]"
                : "border-transparent text-[var(--muted)] hover:text-[var(--fg)]"
            }`}
            onClick={() => setTab("pixel")}
            data-testid="tab-pixel"
          >
            📊 Pixel (Website Events)
          </button>
        </div>

        {tab === "funnel" ? (
          <FunnelTab data={data} days={days} />
        ) : tab === "pixel" ? (
          <PixelTab days={days} />
        ) : (
          <AdsTab
            data={adsData}
            loading={loadingAds}
            onRefresh={() => loadAds(`${days}d`)}
          />
        )}

        <footer className="text-[10px] text-[var(--muted)] pt-4 border-t border-[var(--border)]">
          Data refreshes on page load. Meta Ads Manager has the official spend
          + impressions view — this report focuses on our-side funnel conversion.
        </footer>
      </main>
    </div>
  );
}

// ---- Funnel (our WhatsApp side) --------------------------------------------
function FunnelTab({ data, days }) {
  const t = data?.totals || {};
  return (
    <>
        {/* Totals cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={<Users size={18} weight="fill" />}
            color="#1877F2"
            label="Leads"
            value={t.leads || 0}
            hint="First inbound on WhatsApp"
          />
          <KpiCard
            icon={<ClipboardText size={18} weight="fill" />}
            color="#10B981"
            label="Registrations"
            value={t.registrations || 0}
            hint={`${t.reg_rate || 0}% of leads`}
          />
          <KpiCard
            icon={<CheckCircle size={18} weight="fill" />}
            color="#F59E0B"
            label="Checkouts"
            value={t.checkouts || 0}
            hint={`${t.checkout_rate || 0}% of leads`}
          />
          <KpiCard
            icon={<WhatsappLogo size={18} weight="fill" />}
            color="#25D366"
            label="Conv rate"
            value={`${t.checkout_rate || 0}%`}
            hint="Lead → Checkout"
          />
        </div>

        {/* Daily chart — simple bars */}
        <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading text-sm font-semibold">Daily funnel</h2>
            <span className="text-[10px] text-[var(--muted)]">
              Last {days} days · bars: leads / regs / checkouts
            </span>
          </div>
          <DailyChart rows={data?.daily || []} />
        </section>

        {/* Campaigns table */}
        <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--border)]">
            <h2 className="heading text-sm font-semibold">Performance by campaign</h2>
            <p className="text-[10px] text-[var(--muted)] mt-0.5">
              Grouped by <code>source_id</code> from Meta ad referral
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--bg)] text-[var(--muted)]">
                <tr>
                  <th className="text-left font-medium px-4 py-2">Campaign / Ad</th>
                  <th className="text-right font-medium px-3 py-2">Leads</th>
                  <th className="text-right font-medium px-3 py-2">Regs</th>
                  <th className="text-right font-medium px-3 py-2">Checkouts</th>
                  <th className="text-right font-medium px-3 py-2">Reg %</th>
                  <th className="text-right font-medium px-3 py-2">Conv %</th>
                  <th className="text-left font-medium px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {(data?.campaigns || []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-[var(--muted)] text-xs">
                      No campaign activity yet. Once customers arrive from a
                      WhatsApp click-to-chat ad, rows will appear here.
                    </td>
                  </tr>
                ) : (
                  (data?.campaigns || []).map((c) => (
                    <tr key={c.campaign_id} className="border-t border-[var(--border)]">
                      <td className="px-4 py-2 max-w-[280px]">
                        <div className="flex items-center gap-1.5">
                          {c.kind && (
                            <span
                              className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${
                                c.kind === "ad"
                                  ? "bg-[#DBEAFE] text-[#1E40AF]"
                                  : c.kind === "adset"
                                  ? "bg-[#F3E8FF] text-[#6B21A8]"
                                  : "bg-[#D1FAE5] text-[#065F46]"
                              }`}
                            >
                              {c.kind}
                            </span>
                          )}
                          {c.status && (
                            <span
                              className={`text-[9px] px-1.5 py-0.5 rounded ${
                                c.status === "ACTIVE"
                                  ? "bg-[#D1FAE5] text-[#065F46]"
                                  : "bg-[var(--bg)] text-[var(--muted)]"
                              }`}
                            >
                              {c.status}
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-medium truncate mt-0.5">
                          {c.display_name}
                        </div>
                        {c.parent_campaign && c.parent_campaign !== c.display_name && (
                          <div className="text-[10px] text-[var(--muted)] truncate">
                            ↳ {c.parent_campaign}
                          </div>
                        )}
                        <div className="text-[10px] text-[var(--muted)] font-mono truncate">
                          {c.campaign_id}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.leads}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.registrations}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.checkouts}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#10B981]">
                        {c.reg_rate}%
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-[#F59E0B]">
                        {c.checkout_rate}%
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {c.sample_headline ? (
                          <span className="line-clamp-1 max-w-[200px]" title={c.sample_headline}>
                            {c.sample_headline}
                          </span>
                        ) : (
                          <span className="text-[var(--muted)]">—</span>
                        )}
                        {c.sample_url && (
                          <a
                            href={c.sample_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-1 text-[#1877F2] inline-flex"
                          >
                            <ArrowSquareOut size={10} />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
    </>
  );
}

// ---- Ads Tab (Meta-side reach + spend) -------------------------------------
function AdsTab({ data, loading, onRefresh }) {
  // Awareness mode default — most RodBez ads are reach-focused, not lead-focused.
  // Persist user preference across reloads in localStorage.
  const [recMode, setRecMode] = useState(() => {
    try {
      return localStorage.getItem("rb_rec_mode") || "awareness";
    } catch {
      return "awareness";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("rb_rec_mode", recMode);
    } catch {}
  }, [recMode]);

  // Status filter — default ACTIVE (only active ads at top).
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      return localStorage.getItem("rb_ad_status_filter") || "ACTIVE";
    } catch {
      return "ACTIVE";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("rb_ad_status_filter", statusFilter);
    } catch {}
  }, [statusFilter]);

  // Action state — track which ad/adset is currently mutating
  const [mutating, setMutating] = useState({}); // { [ad_id]: true }
  const [budgetDialog, setBudgetDialog] = useState(null); // { ad, currentRupees }

  if (loading && !data) {
    return (
      <div className="text-center py-12 text-[var(--muted)] text-sm">
        Loading ad insights…
      </div>
    );
  }
  if (!data || data.count === 0) {
    return (
      <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-8 text-center">
        <WarningCircle size={32} className="mx-auto text-[var(--muted)] mb-2" />
        <div className="text-sm font-medium mb-1">No ads synced yet</div>
        <div className="text-xs text-[var(--muted)]">
          Click "Sync names" in the header to pull ad data from Meta Marketing API.
        </div>
      </div>
    );
  }
  const t = data.totals || {};
  const items = data.items || [];
  const awarenessRecs = buildAwarenessRecommendations(items);
  const leadRecs = buildRecommendations(items);
  const recs =
    recMode === "awareness"
      ? awarenessRecs
      : recMode === "lead"
        ? leadRecs
        : [...awarenessRecs, ...leadRecs];
  const anyRecsAvailable = awarenessRecs.length > 0 || leadRecs.length > 0;

  // Filter ads by status; "ACTIVE" only matches strict ACTIVE,
  // "PAUSED" matches anything containing PAUSED (ad/adset/campaign-level).
  const filteredItems = items.filter((ad) => {
    if (statusFilter === "ALL") return true;
    if (statusFilter === "ACTIVE") return ad.status === "ACTIVE";
    if (statusFilter === "PAUSED") return (ad.status || "").includes("PAUSED");
    return true;
  });

  // ---- Action handlers (pause/resume + budget) ----
  const setBusy = (id, val) =>
    setMutating((m) => ({ ...m, [id]: val }));

  const handleToggleStatus = async (ad) => {
    // CAMPAIGN_PAUSED / ADSET_PAUSED can't be reversed at the ad level —
    // user must unpause the parent campaign/adset in Meta Ads Manager first.
    if (ad.status === "CAMPAIGN_PAUSED") {
      toast.error(
        `Parent campaign "${ad.campaign_name || "—"}" is paused. Please unpause it first in Meta Ads Manager.`,
        { duration: 7000 },
      );
      return;
    }
    if (ad.status === "ADSET_PAUSED") {
      toast.error(
        `Parent adset "${ad.adset_name || "—"}" is paused. Please unpause it first in Meta Ads Manager.`,
        { duration: 7000 },
      );
      return;
    }
    const newStatus = ad.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    const verb = newStatus === "PAUSED" ? "Pause" : "Resume";
    if (!window.confirm(`${verb} ad: "${ad.ad_name}"?`)) return;
    setBusy(ad.ad_id, true);
    try {
      const r = await api.post(`/reports/ads/${ad.ad_id}/status`, { status: newStatus });
      const effective = r.data?.effective_status || newStatus;
      // Some statuses (CAMPAIGN_PAUSED, ADSET_PAUSED) can override the
      // ad-level update silently. Surface that to the user instead of
      // misleadingly showing "Resumed successfully" when nothing changed.
      if (newStatus === "ACTIVE" && effective !== "ACTIVE") {
        toast.warning(
          `Status updated, but Meta still reports "${effective}". Parent campaign/adset may also be paused.`,
          { duration: 7000 },
        );
      } else {
        toast.success(`Ad ${newStatus === "PAUSED" ? "paused" : "resumed"} successfully`);
      }
      // Wait a moment for Meta to propagate then refresh
      setTimeout(() => onRefresh(), 1500);
    } catch (e) {
      toast.error(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(ad.ad_id, false);
    }
  };

  const handleOpenBudget = async (ad) => {
    if (!ad.adset_id) {
      toast.error("Adset ID missing — sync campaigns first");
      return;
    }
    setBusy(ad.ad_id, true);
    try {
      const r = await api.get(`/reports/adsets/${ad.adset_id}/budget`);
      const res = r.data || {};
      setBudgetDialog({
        ad,
        currentRupees: res.daily_budget_rupees,
        adsetName: res.name,
      });
    } catch (e) {
      toast.error(
        `Could not fetch budget: ${e?.response?.data?.detail || e.message}`,
      );
    } finally {
      setBusy(ad.ad_id, false);
    }
  };
  return (
    <>
      {/* Totals cards — Meta side */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <KpiCard
          icon={<ChartLineUp size={18} weight="fill" />}
          color="#1877F2"
          label="Impressions"
          value={(t.impressions || 0).toLocaleString("en-IN")}
          hint="Total views"
        />
        <KpiCard
          icon={<Users size={18} weight="fill" />}
          color="#10B981"
          label="Reach"
          value={(t.reach || 0).toLocaleString("en-IN")}
          hint="Unique people"
        />
        <KpiCard
          icon={<Lightning size={18} weight="fill" />}
          color="#F59E0B"
          label="Clicks"
          value={(t.clicks || 0).toLocaleString("en-IN")}
          hint={
            t.impressions
              ? `CTR ${((t.clicks / t.impressions) * 100).toFixed(2)}%`
              : ""
          }
        />
        <KpiCard
          icon={<Tag size={18} weight="fill" />}
          color="#EF4444"
          label="Spend"
          value={`₹${(t.spend || 0).toLocaleString("en-IN")}`}
          hint={data.window === "7d" ? "Last 7 days" : "Last 30 days"}
        />
        <KpiCard
          icon={<WhatsappLogo size={18} weight="fill" />}
          color="#25D366"
          label="WA Leads"
          value={t.leads || 0}
          hint="From ads"
        />
        <KpiCard
          icon={<CheckCircle size={18} weight="fill" />}
          color="#6366F1"
          label="Cost / Lead"
          value={t.cost_per_lead != null ? `₹${t.cost_per_lead}` : "—"}
          hint="Spend ÷ Leads"
        />
      </div>

      {/* Recommendations widget */}
      {anyRecsAvailable && (
        <section className="rounded-lg border border-[#6366F1]/30 bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] dark:from-[#312E81]/20 dark:to-[#4C1D95]/20 p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="h-7 w-7 rounded-md bg-[#6366F1] grid place-items-center text-white">
              <Lightning size={14} weight="fill" />
            </div>
            <h2 className="heading text-sm font-semibold">Smart recommendations</h2>
            {/* Mode toggle — Awareness / Lead / Both */}
            <div
              className="ml-2 inline-flex rounded-md border border-[var(--border)] bg-[var(--surface)] p-0.5 text-[10px] font-medium"
              data-testid="rec-mode-toggle"
            >
              {[
                { k: "awareness", label: "📢 Awareness" },
                { k: "lead", label: "🎯 Lead" },
                { k: "both", label: "Both" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setRecMode(opt.k)}
                  data-testid={`rec-mode-${opt.k}`}
                  className={`px-2 py-1 rounded-[4px] transition ${
                    recMode === opt.k
                      ? "bg-[#6366F1] text-white"
                      : "text-[var(--muted)] hover:text-[var(--text)]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-[10px] text-[var(--muted)] ml-auto">
              Based on {data.window === "7d" ? "last 7" : "last 30"} days
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {recs.length === 0 ? (
              <div
                className="md:col-span-2 text-center text-[11px] text-[var(--muted)] py-3"
                data-testid="rec-empty-state"
              >
                Is mode me koi suggestion nahi —{" "}
                <button
                  type="button"
                  onClick={() => setRecMode(recMode === "awareness" ? "lead" : "awareness")}
                  className="underline text-[#6366F1]"
                >
                  {recMode === "awareness" ? "🎯 Lead mode" : "📢 Awareness mode"}
                </button>{" "}
                try karo.
              </div>
            ) : (
              recs.map((r, i) => (
                <div
                  key={`${r.kind}-${i}`}
                  className="flex items-start gap-2 p-2.5 rounded-md bg-[var(--surface)] border border-[var(--border)]"
                  data-testid={`rec-${r.kind}-${i}`}
                >
                  <div className={`text-lg shrink-0 ${r.color}`}>{r.emoji}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold mb-0.5">{r.title}</div>
                    <div className="text-[11px] text-[var(--muted)] leading-snug">
                      {r.detail}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {/* Ads table */}
      <section className="rounded-lg border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="heading text-sm font-semibold">
              {statusFilter === "ACTIVE"
                ? `Active ads (${filteredItems.length})`
                : statusFilter === "PAUSED"
                  ? `Paused ads (${filteredItems.length})`
                  : `All ads (${filteredItems.length})`}
            </h2>
            <p className="text-[10px] text-[var(--muted)] mt-0.5">
              Meta-side insights for {data.window === "7d" ? "last 7 days" : "last 30 days"} · sorted by spend
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Status filter pill toggle */}
            <div
              className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-[10px] font-medium"
              data-testid="ad-status-filter"
            >
              {[
                { k: "ACTIVE", label: "🟢 Active" },
                { k: "PAUSED", label: "⏸️ Paused" },
                { k: "ALL", label: "All" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setStatusFilter(opt.k)}
                  data-testid={`ad-filter-${opt.k.toLowerCase()}`}
                  className={`px-2 py-1 rounded-[4px] transition ${
                    statusFilter === opt.k
                      ? "bg-[#10B981] text-white"
                      : "text-[var(--muted)] hover:text-[var(--text)]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading}>
              <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)] text-[var(--muted)]">
              <tr>
                <th className="text-left font-medium px-4 py-2">Ad / Campaign</th>
                <th className="text-right font-medium px-3 py-2">Budget</th>
                <th className="text-right font-medium px-3 py-2">Reach</th>
                <th className="text-right font-medium px-3 py-2">Impr</th>
                <th className="text-right font-medium px-3 py-2">Clicks</th>
                <th className="text-right font-medium px-3 py-2">CTR</th>
                <th className="text-right font-medium px-3 py-2">Spend</th>
                <th className="text-right font-medium px-3 py-2">Leads</th>
                <th className="text-right font-medium px-3 py-2">CPL</th>
                <th className="text-right font-medium px-3 py-2 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center py-8 text-[var(--muted)] text-xs"
                  >
                    Is filter me koi ad nahi —{" "}
                    <button
                      type="button"
                      onClick={() => setStatusFilter("ALL")}
                      className="underline text-[#10B981]"
                    >
                      All ads
                    </button>{" "}
                    dekho.
                  </td>
                </tr>
              ) : (
                filteredItems.map((ad) => (
                <tr key={ad.ad_id} className="border-t border-[var(--border)]">
                  <td className="px-4 py-2 max-w-[260px]">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {ad.status && (
                        <span
                          className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${
                            ad.status === "ACTIVE"
                              ? "bg-[#D1FAE5] text-[#065F46]"
                              : ad.status.includes("PAUSED")
                              ? "bg-[#FEF3C7] text-[#92400E]"
                              : "bg-[var(--bg)] text-[var(--muted)]"
                          }`}
                        >
                          {ad.status.replace("CAMPAIGN_", "").replace("ADSET_", "")}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-medium truncate" title={ad.ad_name}>
                      {ad.ad_name || "(unnamed)"}
                    </div>
                    {ad.campaign_name && ad.campaign_name !== ad.ad_name && (
                      <div className="text-[10px] text-[var(--muted)] truncate" title={ad.campaign_name}>
                        ↳ {ad.campaign_name}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {ad.daily_budget_rupees != null ? (
                      <div>
                        <div className="font-medium">
                          ₹{ad.daily_budget_rupees.toLocaleString("en-IN")}
                        </div>
                        <div className="text-[9px] text-[var(--muted)]">/day</div>
                      </div>
                    ) : ad.lifetime_budget_rupees != null ? (
                      <div>
                        <div className="font-medium">
                          ₹{ad.lifetime_budget_rupees.toLocaleString("en-IN")}
                        </div>
                        <div className="text-[9px] text-[var(--muted)]">lifetime</div>
                      </div>
                    ) : (
                      <span className="text-[var(--muted)] text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {(ad.reach || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {(ad.impressions || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{ad.clicks || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#1877F2]">
                    {ad.ctr ? `${Number(ad.ctr).toFixed(2)}%` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    ₹{(ad.spend || 0).toLocaleString("en-IN")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#10B981]">
                    {ad.leads || 0}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {ad.cost_per_lead != null ? `₹${ad.cost_per_lead}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(ad)}
                        disabled={!!mutating[ad.ad_id]}
                        data-testid={`ad-toggle-${ad.ad_id}`}
                        title={ad.status === "ACTIVE" ? "Pause this ad" : "Resume this ad"}
                        className={`text-[10px] px-2 py-1 rounded border transition ${
                          ad.status === "ACTIVE"
                            ? "border-[#FCD34D] text-[#92400E] hover:bg-[#FEF3C7]"
                            : ad.status?.includes("PAUSED")
                              ? "border-[#86EFAC] text-[#065F46] hover:bg-[#D1FAE5]"
                              : "border-[var(--border)] text-[var(--muted)] cursor-not-allowed"
                        } disabled:opacity-50`}
                      >
                        {mutating[ad.ad_id]
                          ? "…"
                          : ad.status === "ACTIVE"
                            ? "⏸ Pause"
                            : ad.status?.includes("PAUSED")
                              ? "▶ Resume"
                              : "—"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenBudget(ad)}
                        disabled={!!mutating[ad.ad_id] || !ad.adset_id}
                        data-testid={`ad-budget-${ad.ad_id}`}
                        title="Edit daily budget"
                        className="text-[10px] px-2 py-1 rounded border border-[#A5B4FC] text-[#3730A3] hover:bg-[#EEF2FF] disabled:opacity-50"
                      >
                        💰 Budget
                      </button>
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Budget edit dialog */}
      {budgetDialog && (
        <BudgetDialog
          ad={budgetDialog.ad}
          currentRupees={budgetDialog.currentRupees}
          adsetName={budgetDialog.adsetName}
          onClose={() => setBudgetDialog(null)}
          onSaved={() => {
            setBudgetDialog(null);
            onRefresh();
          }}
        />
      )}
    </>
  );
}

function KpiCard({ icon, color, label, value, hint }) {
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-3 md:p-4">
      <div className="flex items-center gap-2">
        <div
          className="h-8 w-8 rounded-md grid place-items-center text-white shrink-0"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>
        <div className="text-xs text-[var(--muted)] font-medium">{label}</div>
      </div>
      <div className="text-2xl md:text-3xl font-bold tabular-nums mt-2">{value}</div>
      <div className="text-[10px] text-[var(--muted)] mt-0.5 truncate">{hint}</div>
    </div>
  );
}

function DailyChart({ rows }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--muted)] text-xs">
        No events in this range yet.
      </div>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r.leads || 0));
  return (
    <div className="flex items-end gap-1 md:gap-2 h-40 overflow-x-auto pb-2">
      {rows.map((r) => (
        <div
          key={r.day}
          className="flex flex-col items-center gap-1 min-w-[32px] md:min-w-[40px] shrink-0"
          title={`${r.day} — ${r.leads} leads · ${r.registrations} regs · ${r.checkouts} checkouts`}
        >
          <div className="w-full h-28 relative flex items-end gap-0.5">
            <div
              className="flex-1 bg-[#1877F2] rounded-sm"
              style={{ height: `${((r.leads || 0) / max) * 100}%` }}
            />
            <div
              className="flex-1 bg-[#10B981] rounded-sm"
              style={{ height: `${((r.registrations || 0) / max) * 100}%` }}
            />
            <div
              className="flex-1 bg-[#F59E0B] rounded-sm"
              style={{ height: `${((r.checkouts || 0) / max) * 100}%` }}
            />
          </div>
          <div className="text-[9px] text-[var(--muted)] font-mono">
            {r.day.slice(5)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Budget edit dialog ---------------------------------------------------
function BudgetDialog({ ad, currentRupees, adsetName, onClose, onSaved }) {
  const [mode, setMode] = useState("preset"); // 'preset' | 'custom'
  const [customValue, setCustomValue] = useState(
    currentRupees ? String(currentRupees) : "",
  );
  const [saving, setSaving] = useState(false);

  const presets = [
    { label: "−25%", delta: -25, color: "border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEE2E2]" },
    { label: "−10%", delta: -10, color: "border-[#FCD34D] text-[#92400E] hover:bg-[#FEF3C7]" },
    { label: "+10%", delta: 10, color: "border-[#86EFAC] text-[#065F46] hover:bg-[#D1FAE5]" },
    { label: "+25%", delta: 25, color: "border-[#10B981] text-white bg-[#10B981] hover:bg-[#059669]" },
  ];

  const calcNew = (delta) => {
    if (currentRupees == null) return null;
    return Math.round(currentRupees * (1 + delta / 100));
  };

  const apply = async (payload, label) => {
    if (!window.confirm(`${label}\n\nApply?`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/reports/adsets/${ad.adset_id}/budget`, payload);
      toast.success(
        `Budget updated to ₹${r.data?.new_daily_budget_rupees || "?"} per day`,
      );
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update budget");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="budget-dialog">
        <DialogHeader>
          <DialogTitle>Daily Budget</DialogTitle>
          <DialogDescription className="text-xs">
            Adset: <span className="font-medium">{adsetName || ad.adset_name || "—"}</span>
            <br />
            Ad: <span className="font-medium">{ad.ad_name}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* Current */}
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg)] p-3">
            <div className="text-[10px] text-[var(--muted)] uppercase">Current daily budget</div>
            <div className="text-2xl font-bold tabular-nums">
              {currentRupees != null ? `₹${currentRupees.toLocaleString("en-IN")}` : "—"}
            </div>
          </div>

          {/* Mode toggle */}
          <div className="inline-flex rounded-md border border-[var(--border)] p-0.5 text-[10px] font-medium">
            <button
              type="button"
              onClick={() => setMode("preset")}
              className={`px-3 py-1 rounded-[4px] transition ${
                mode === "preset" ? "bg-[#6366F1] text-white" : "text-[var(--muted)]"
              }`}
              data-testid="budget-mode-preset"
            >
              Quick ±%
            </button>
            <button
              type="button"
              onClick={() => setMode("custom")}
              className={`px-3 py-1 rounded-[4px] transition ${
                mode === "custom" ? "bg-[#6366F1] text-white" : "text-[var(--muted)]"
              }`}
              data-testid="budget-mode-custom"
            >
              Custom ₹
            </button>
          </div>

          {/* Presets */}
          {mode === "preset" && (
            <div className="grid grid-cols-2 gap-2" data-testid="budget-presets">
              {presets.map((p) => {
                const newVal = calcNew(p.delta);
                return (
                  <button
                    key={p.label}
                    type="button"
                    disabled={saving || currentRupees == null}
                    onClick={() =>
                      apply(
                        { delta_percent: p.delta },
                        `Change daily budget by ${p.label}\nFrom ₹${currentRupees} → ₹${newVal}/day`,
                      )
                    }
                    className={`px-3 py-2 rounded-md border text-xs font-medium transition disabled:opacity-50 ${p.color}`}
                    data-testid={`budget-preset-${p.delta}`}
                  >
                    <div>{p.label}</div>
                    <div className="text-[10px] opacity-80 mt-0.5">
                      {newVal != null ? `→ ₹${newVal}` : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Custom */}
          {mode === "custom" && (
            <div className="space-y-2">
              <label className="text-[10px] text-[var(--muted)] uppercase">
                New daily budget (₹)
              </label>
              <Input
                type="number"
                min={40}
                step={10}
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder="e.g. 250"
                disabled={saving}
                data-testid="budget-custom-input"
              />
              <div className="text-[10px] text-[var(--muted)]">
                Minimum ₹40/day · enter the absolute new amount
              </div>
              <Button
                size="sm"
                disabled={
                  saving ||
                  !customValue ||
                  Number(customValue) < 40 ||
                  Number(customValue) === currentRupees
                }
                onClick={() =>
                  apply(
                    { daily_budget_rupees: Number(customValue) },
                    `Set daily budget to ₹${customValue}/day\n(was ₹${currentRupees}/day)`,
                  )
                }
                data-testid="budget-custom-save"
                className="w-full"
              >
                {saving ? "Saving…" : `Set to ₹${customValue || "?"}/day`}
              </Button>
            </div>
          )}

          <div className="text-[10px] text-[var(--muted)] border-t border-[var(--border)] pt-2">
            ⚠️ Budget changes apply directly to Meta. Aaj se hi naya rate lagega.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}




function CampaignNamesDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/reports/campaigns/list");
      setItems(r.data?.items || []);
      const map = {};
      (r.data?.items || []).forEach((x) => {
        map[x.campaign_id] = x.name || "";
      });
      setDrafts(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const save = async (campaignId) => {
    const name = (drafts[campaignId] || "").trim();
    setSaving((s) => ({ ...s, [campaignId]: true }));
    try {
      await api.post("/reports/campaigns/rename", { campaign_id: campaignId, name });
      toast.success(name ? `Renamed → "${name}"` : "Name cleared");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving((s) => ({ ...s, [campaignId]: false }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1.5"
          data-testid="name-campaigns-btn"
          title="Name your ad campaigns"
        >
          <Tag size={12} weight="fill" />
          <span className="hidden md:inline">Name campaigns</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag size={18} weight="fill" className="text-[#1877F2]" />
            Name your campaigns
          </DialogTitle>
          <DialogDescription>
            Meta ke raw campaign IDs (jaise{" "}
            <code className="text-[10px]">120201234567</code>) ko readable naam
            do — e.g. "Diwali Sale - Bihar". Aapke har ad/adset ka ID yahan
            apne aap aa jata hai jab koi customer us ad se WhatsApp pe aata hai.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading ? (
            <div className="text-center py-10 text-[var(--muted)] text-xs">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-[var(--muted)] text-xs">
              Abhi tak kisi ad se lead nahi aayi. Jaise hi koi customer aapke
              click-to-WhatsApp ad se aayega, yahan uska campaign ID aa jayega
              aur aap use naam de sakte ho.
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((c) => {
                const draft = drafts[c.campaign_id] ?? c.name ?? "";
                const hasChanges = draft.trim() !== (c.name || "").trim();
                return (
                  <div
                    key={c.campaign_id}
                    className="rounded-md border border-[var(--border)] bg-[var(--surface)] p-3"
                    data-testid={`campaign-row-${c.campaign_id}`}
                  >
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-[10px] font-mono text-[var(--muted)] bg-[var(--bg)] px-1.5 py-0.5 rounded">
                        {c.campaign_id}
                      </span>
                      <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#F3E8FF] text-[#6B21A8]">
                        {c.source_type || "ad"}
                      </span>
                      {c.kind === "manual" && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#D1FAE5] text-[#065F46]">
                          Named
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col md:flex-row gap-2">
                      <Input
                        value={draft}
                        onChange={(e) =>
                          setDrafts((d) => ({ ...d, [c.campaign_id]: e.target.value }))
                        }
                        placeholder="e.g. Diwali Sale - Bihar Region"
                        className="flex-1 h-9 text-sm"
                        data-testid={`campaign-name-input-${c.campaign_id}`}
                      />
                      <Button
                        size="sm"
                        className="h-9 shrink-0"
                        onClick={() => save(c.campaign_id)}
                        disabled={!hasChanges || saving[c.campaign_id]}
                        data-testid={`campaign-save-${c.campaign_id}`}
                      >
                        {saving[c.campaign_id] ? "Saving…" : "Save"}
                      </Button>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--muted)] flex-wrap">
                      <span className="flex items-center gap-1">
                        <Users size={11} weight="fill" className="text-[#1877F2]" />
                        {c.leads} leads
                      </span>
                      <span className="flex items-center gap-1">
                        <ClipboardText size={11} weight="fill" className="text-[#10B981]" />
                        {c.registrations} regs
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle size={11} weight="fill" className="text-[#F59E0B]" />
                        {c.checkouts} checkouts
                      </span>
                      {c.last_seen && (
                        <span className="ml-auto">
                          Last: {new Date(c.last_seen).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {c.sample_headline && (
                      <div className="text-[11px] text-[var(--muted)] mt-1 italic line-clamp-1">
                        "{c.sample_headline}"
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="border-t border-[var(--border)] pt-3 flex items-center justify-between text-[11px] text-[var(--muted)]">
          <span>{items.length} tracked campaigns</span>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
            <ArrowsClockwise size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Heuristic-based ad recommendation engine. Analyzes each running ad against
// industry benchmarks and calls out ads that are wasting money or quietly
// over-performing.  Pure JS — no ML / LLM calls.
//
// Rules used:
//   * Waste:     spend >= ₹200 AND (CTR < 0.5%) — 0.5% is Facebook's low bar
//   * Hidden gem: CTR >= 1.5% AND spend < ₹500  — performing but underfunded
//   * Best CPL: lowest cost_per_lead among ads with >=5 leads (worth scaling)
//   * No leads: spend >= ₹500 AND leads === 0  — link/landing page issue
//   * Paused winner: status=PAUSED AND CTR >= 1.5% AND leads >= 3
function buildRecommendations(ads) {
  if (!ads || ads.length === 0) return [];
  const running = ads.filter((a) => a.status === "ACTIVE");
  const recs = [];

  // 1. Wasteful ads (high spend, low CTR)
  const wasteful = running
    .filter((a) => a.spend >= 200 && Number(a.ctr) < 0.5)
    .sort((a, b) => b.spend - a.spend)
    .slice(0, 2);
  for (const a of wasteful) {
    recs.push({
      kind: "waste",
      emoji: "💸",
      color: "text-[#DC2626]",
      title: `Budget leak: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `₹${a.spend} spent with only ${Number(a.ctr).toFixed(2)}% CTR. Pause ya creative refresh karo — paisa waste ho raha hai.`,
    });
  }

  // 2. Hidden gems (great CTR, small spend)
  const gems = running
    .filter((a) => Number(a.ctr) >= 1.5 && a.spend > 0 && a.spend < 500)
    .sort((a, b) => Number(b.ctr) - Number(a.ctr))
    .slice(0, 2);
  for (const a of gems) {
    recs.push({
      kind: "gem",
      emoji: "💎",
      color: "text-[#10B981]",
      title: `Scale up: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `CTR ${Number(a.ctr).toFixed(2)}% is strong but budget only ₹${a.spend}. Iske adset ka daily budget badhao — zyada leads milenge.`,
    });
  }

  // 3. Best CPL performer (lowest cost per lead with real data)
  const cplAds = ads.filter((a) => a.cost_per_lead != null && a.leads >= 5);
  if (cplAds.length > 0) {
    const best = cplAds.reduce((x, y) => (x.cost_per_lead < y.cost_per_lead ? x : y));
    recs.push({
      kind: "best_cpl",
      emoji: "🏆",
      color: "text-[#F59E0B]",
      title: `Best CPL: "${(best.ad_name || "").slice(0, 40)}"`,
      detail: `Sirf ₹${best.cost_per_lead} per lead on ${best.leads} leads. Iska creative aur audience clone karke aur ads banao.`,
    });
  }

  // 4. Spend without leads (link / conversion issue)
  const silent = running
    .filter((a) => a.spend >= 500 && a.leads === 0)
    .sort((a, b) => b.spend - a.spend)
    .slice(0, 1);
  for (const a of silent) {
    recs.push({
      kind: "silent",
      emoji: "🔕",
      color: "text-[#F59E0B]",
      title: `No leads: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `₹${a.spend} spend hua but WhatsApp pe ek bhi lead nahi aayi. Click-to-WhatsApp link check karo — shayad broken hai.`,
    });
  }

  // 5. Paused winner — bring back
  const pausedWinners = ads
    .filter(
      (a) =>
        (a.status || "").includes("PAUSED") &&
        Number(a.ctr) >= 1.5 &&
        a.leads >= 3,
    )
    .slice(0, 1);
  for (const a of pausedWinners) {
    recs.push({
      kind: "revive",
      emoji: "♻️",
      color: "text-[#6366F1]",
      title: `Revive: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `Paused hai but pichli run mein CTR ${Number(a.ctr).toFixed(2)}% aur ${a.leads} leads dilaye. Dobara chalao.`,
    });
  }

  return recs;
}


// ---------------------------------------------------------------------------
// Awareness-focused recommendation engine.
// Most RodBez ads are reach/awareness-focused (not lead-conversion).
// Industry benchmarks (Meta India, FY25):
//   * CPM (cost per 1000 impressions): ₹40-₹120 typical for reach campaigns
//     - excellent: < ₹50, expensive: > ₹150
//   * Frequency (impressions / reach): 1.5-2.5 healthy, > 3.5 = ad fatigue
//   * Reach efficiency: 1 rupee should reach >= 8-10 unique people minimum
// ---------------------------------------------------------------------------
function buildAwarenessRecommendations(ads) {
  if (!ads || ads.length === 0) return [];
  const running = ads.filter((a) => a.status === "ACTIVE" && a.has_insights);
  const recs = [];

  // helper: frequency = impressions / reach
  const freq = (a) =>
    a.reach > 0 ? Number((a.impressions / a.reach).toFixed(2)) : 0;
  // helper: reach per ₹1 = reach / spend
  const reachPerRupee = (a) => (a.spend > 0 ? a.reach / a.spend : 0);

  // 1. Best Reach Efficiency — lowest CPM among ads with real spend
  const efficient = running
    .filter((a) => a.spend >= 100 && a.cpm > 0)
    .sort((a, b) => Number(a.cpm) - Number(b.cpm));
  if (efficient.length > 0) {
    const best = efficient[0];
    recs.push({
      kind: "best_cpm",
      emoji: "🏆",
      color: "text-[#10B981]",
      title: `Best reach efficiency: "${(best.ad_name || "").slice(0, 40)}"`,
      detail: `CPM sirf ₹${Number(best.cpm).toFixed(0)} — ${(best.reach || 0).toLocaleString("en-IN")} unique log ₹${best.spend} me. Iska budget badhao, sasti reach milegi.`,
    });
  }

  // 2. Expensive Reach — high CPM with high spend
  const expensive = running
    .filter((a) => a.spend >= 500 && Number(a.cpm) > 150)
    .sort((a, b) => Number(b.cpm) - Number(a.cpm))
    .slice(0, 2);
  for (const a of expensive) {
    recs.push({
      kind: "expensive_cpm",
      emoji: "💸",
      color: "text-[#DC2626]",
      title: `Expensive reach: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `CPM ₹${Number(a.cpm).toFixed(0)} bohot zyada hai (₹${a.spend} kharcha). Audience refresh karo ya creative change karo.`,
    });
  }

  // 3. High Frequency Warning — same person ko bar bar dikh raha hai
  const fatigued = running
    .filter((a) => a.reach >= 5000 && freq(a) >= 3.5)
    .sort((a, b) => freq(b) - freq(a))
    .slice(0, 2);
  for (const a of fatigued) {
    recs.push({
      kind: "fatigue",
      emoji: "📢",
      color: "text-[#F59E0B]",
      title: `Ad fatigue: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `Frequency ${freq(a)}× — ek hi banda ${freq(a)} baar dekha. Naya creative ya wider audience banao, warna log irritate honge.`,
    });
  }

  // 4. Best Reach Volume — biggest unique audience reached
  const topReach = running
    .filter((a) => a.reach >= 10000)
    .sort((a, b) => b.reach - a.reach);
  if (topReach.length > 0) {
    const a = topReach[0];
    recs.push({
      kind: "top_reach",
      emoji: "🔥",
      color: "text-[#EF4444]",
      title: `Top reach: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `${(a.reach || 0).toLocaleString("en-IN")} unique log dekhe (CPM ₹${Number(a.cpm).toFixed(0)}). Awareness campaign winner — budget aur badhao.`,
    });
  }

  // 5. Low Reach Efficiency — high spend but low reach (poor targeting)
  const lowReach = running
    .filter(
      (a) => a.spend >= 500 && a.reach > 0 && reachPerRupee(a) < 5,
    )
    .sort((a, b) => reachPerRupee(a) - reachPerRupee(b))
    .slice(0, 1);
  for (const a of lowReach) {
    recs.push({
      kind: "low_reach",
      emoji: "📉",
      color: "text-[#F59E0B]",
      title: `Poor reach: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `₹${a.spend} me sirf ${(a.reach || 0).toLocaleString("en-IN")} log. ₹1 me ${reachPerRupee(a).toFixed(1)} log — bahut kam. Audience bohot narrow hai, broaden karo.`,
    });
  }

  // 6. Paused Awareness Winner — paused ad with low CPM previously
  const pausedAware = ads
    .filter(
      (a) =>
        (a.status || "").includes("PAUSED") &&
        a.has_insights &&
        a.reach >= 5000 &&
        Number(a.cpm) > 0 &&
        Number(a.cpm) < 100,
    )
    .sort((a, b) => Number(a.cpm) - Number(b.cpm))
    .slice(0, 1);
  for (const a of pausedAware) {
    recs.push({
      kind: "revive_aware",
      emoji: "♻️",
      color: "text-[#6366F1]",
      title: `Revive winner: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `Paused hai but CPM sirf ₹${Number(a.cpm).toFixed(0)} aur ${(a.reach || 0).toLocaleString("en-IN")} reach diya tha. Dobara chalao.`,
    });
  }

  // 7. Healthy Frequency callout — sweet spot 1.5-2.5
  const sweetSpot = running.filter(
    (a) => a.reach >= 10000 && freq(a) >= 1.5 && freq(a) <= 2.5,
  );
  if (sweetSpot.length > 0 && fatigued.length === 0) {
    const a = sweetSpot.sort((x, y) => y.reach - x.reach)[0];
    recs.push({
      kind: "healthy_freq",
      emoji: "✅",
      color: "text-[#10B981]",
      title: `Healthy frequency: "${(a.ad_name || "").slice(0, 40)}"`,
      detail: `Frequency ${freq(a)}× — ideal range. Ad fresh hai, log irritate nahi ho rahe. Continue karo.`,
    });
  }

  return recs;
}

// ============================================================================
// PixelTab — RodBez Pixel events from Meta Graph API
// ============================================================================
function PixelTab({ days = 7 }) {
  const [overview, setOverview] = useState(null);
  const [stats, setStats] = useState(null);
  const [aggregation, setAggregation] = useState("event");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Map the page-level day toggle (7 / 30 / 90) to Meta's `window`
  // string. Anything outside the supported set falls back to 24h so
  // the chart still has fresh data.
  const window = days === 7 ? "7d" : days === 30 ? "30d" : days === 90 ? "90d" : "24h";
  const windowLabel = window === "24h" ? "Last 24h" :
    window === "7d" ? "Last 7 days" :
    window === "30d" ? "Last 30 days" : "Last 90 days";

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [ov, st] = await Promise.all([
        api.get("/reports/pixel/overview"),
        api.get(`/reports/pixel/stats?aggregation=${aggregation}&limit=200&window=${window}`),
      ]);
      setOverview(ov.data);
      setStats(st.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregation, window]);

  // Flatten Meta's hourly buckets into one event-name × count map.
  const eventTotals = (() => {
    const m = {};
    for (const bucket of stats?.data || []) {
      for (const row of bucket.data || []) {
        const k = row.value || "—";
        m[k] = (m[k] || 0) + (row.count || 0);
      }
    }
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  })();
  const grandTotal = eventTotals.reduce((a, [, c]) => a + c, 0);

  // Show all buckets returned by Meta — at 24h window these are
  // hourly (24 bars), at 7d/30d/90d Meta switches to daily granularity
  // so we get ~7, ~30 or ~90 bars respectively.
  const last24 = (stats?.data || []).map((b) => ({
    time: window === "24h"
      ? (b.start_time?.slice(11, 16) || "—")
      : (b.start_time?.slice(5, 10) || "—"),  // MM-DD
    count: (b.data || []).reduce((s, r) => s + (r.count || 0), 0),
  }));
  const maxCount = Math.max(1, ...last24.map((x) => x.count));

  return (
    <div className="space-y-4" data-testid="pixel-tab">
      <div className="border border-[var(--border)] rounded-lg p-4 bg-[var(--surface)]">
        {loading && !overview ? (
          <div className="text-sm text-[var(--muted)]">Loading pixel data…</div>
        ) : err ? (
          <div className="text-sm text-[#DC2626] flex items-center gap-2">
            ⚠️ {err}
            <Button size="sm" variant="outline" onClick={load} className="ml-2 h-7 text-[11px]">
              Retry
            </Button>
          </div>
        ) : overview ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold">📊 {overview.name}</span>
                {overview.first_party_cookie_status === "first_party_cookie_enabled" && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46] font-bold">
                    1st-party cookies ON
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[var(--muted)] mt-0.5">
                ID: {overview.id} · Owner: {overview.owner_business?.name || "—"} ·
                Last fired: {overview.last_fired_time ? new Date(overview.last_fired_time).toLocaleString("en-IN") : "Never"}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={load}
              disabled={loading}
              className="h-8 gap-1.5"
              data-testid="pixel-refresh"
            >
              {loading ? "Refreshing…" : "↻ Refresh"}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {[
          { k: "event", label: "Event" },
          { k: "browser", label: "Browser" },
          { k: "device_os", label: "OS" },
          { k: "device_type", label: "Device" },
          { k: "host", label: "Domain" },
          { k: "url", label: "URL" },
        ].map((opt) => (
          <button
            key={opt.k}
            onClick={() => setAggregation(opt.k)}
            data-testid={`pixel-agg-${opt.k}`}
            className={`px-2.5 py-1 rounded text-[11px] font-medium transition ${
              aggregation === opt.k
                ? "bg-[#1877F2] text-white"
                : "bg-[var(--surface)] text-[var(--muted)] hover:text-[var(--fg)] border border-[var(--border)]"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border border-[var(--border)] rounded-lg p-4 bg-[var(--surface)]">
          <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-bold">Total events</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{grandTotal.toLocaleString("en-IN")}</div>
          <div className="text-[10px] text-[var(--muted)] mt-0.5">{windowLabel}</div>
        </div>
        <div className="border border-[var(--border)] rounded-lg p-4 bg-[var(--surface)]">
          <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-bold">
            Top {aggregation.replace("_", " ")}
          </div>
          {eventTotals.slice(0, 1).map(([k, c]) => (
            <div key={k}>
              <div className="text-base font-semibold mt-1 truncate">{k}</div>
              <div className="text-[11px] text-[var(--muted)]">
                {c.toLocaleString("en-IN")} events ({((c / Math.max(1, grandTotal)) * 100).toFixed(1)}%)
              </div>
            </div>
          ))}
        </div>
        <div className="border border-[var(--border)] rounded-lg p-4 bg-[var(--surface)]">
          <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] font-bold">Unique values</div>
          <div className="text-2xl font-bold tabular-nums mt-1">{eventTotals.length}</div>
          <div className="text-[10px] text-[var(--muted)] mt-0.5">Distinct {aggregation}s in window</div>
        </div>
      </div>

      {last24.length > 0 && (
        <div className="border border-[var(--border)] rounded-lg p-4 bg-[var(--surface)]">
          <div className="text-[12px] font-semibold text-[var(--muted)] mb-2">
            {windowLabel} timeline
            <span className="opacity-60 ml-1 text-[10px] font-normal">
              ({window === "24h" ? "hourly" : "daily"} buckets)
            </span>
          </div>
          <div className="flex items-end gap-0.5 h-20">
            {last24.map((b, i) => (
              <div
                key={i}
                title={`${b.time} — ${b.count} events`}
                className="flex-1 bg-[#1877F2] rounded-t opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                style={{ height: `${(b.count / maxCount) * 100}%`, minHeight: "1px" }}
              />
            ))}
          </div>
          <div className="flex justify-between text-[9px] text-[var(--muted)] mt-1">
            <span>{last24[0]?.time}</span>
            <span>{last24[Math.floor(last24.length / 2)]?.time}</span>
            <span>{last24[last24.length - 1]?.time}</span>
          </div>
        </div>
      )}

      <div className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-[var(--muted)] text-[11px] uppercase tracking-wide">
            <tr>
              <th className="text-left font-medium px-4 py-2">{aggregation.replace("_", " ")}</th>
              <th className="text-right font-medium px-4 py-2">Events</th>
              <th className="text-right font-medium px-4 py-2">% of total</th>
            </tr>
          </thead>
          <tbody>
            {eventTotals.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-8 text-[var(--muted)] text-xs">
                  No pixel events received yet.
                </td>
              </tr>
            ) : (
              eventTotals.map(([k, c]) => {
                const pct = (c / Math.max(1, grandTotal)) * 100;
                return (
                  <tr key={k} className="border-t border-[var(--border)] hover:bg-[var(--bg)]/40">
                    <td className="px-4 py-2 font-medium truncate max-w-md" title={k}>
                      {k}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-[var(--muted)] text-xs">{pct.toFixed(1)}%</span>
                        <div className="w-16 h-1.5 rounded bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full bg-[#1877F2]"
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

